import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { take } from 'rxjs/operators';
import { AppState } from '../../../app.service';
import { AppVars } from '../../../app.vars';
import { AuthService } from '../../auth/auth.service';
import { AppointmentDataService } from '../appointment-data/appointment-data.service';
import { PatientDataService } from '../patient-data/patient-data.service';
import { CaseDataOptions, ComposedCaseData, RawCaseNodes } from './case-data.model';
import { CaseDataTransformer } from './case-data-transformer';

@Injectable({
  providedIn: 'root',
})
export class CaseDataComposerService {
  constructor(
    private pds: PatientDataService,
    private ads: AppointmentDataService,
    private auth: AuthService,
    private db: AngularFireDatabase,
    private _as: AppState
  ) {}

  /**
   * Universal case data aggregator for OrganizeMED.
   * Retrieves and standardizes case data from multiple RTDB nodes into a dual-key ComposedCaseData
   * dictionary consumable by both HTML TinyMCE Reports and PDF DWC AcroForms.
   *
   * @param options CaseDataOptions with patientKey and optional appointmentKey & ingestion toggles
   */
  public async getComposedCaseData(options: CaseDataOptions): Promise<ComposedCaseData> {
    if (!options || !options.patientKey) {
      throw new Error('[CaseDataComposerService] patientKey is required');
    }

    const orgCode = this._as.get(AppVars.ORG_CODE);
    const user = this._as.get(AppVars.USER_ACCOUNT) || {};
    const patientKey = options.patientKey;

    // 1. Concurrent Ingestion of Core Nodes
    const [patient, orgData, appointments, providers] = await Promise.all([
      this.pds.getPatient(patientKey).pipe(take(1)).toPromise(),
      this.auth.getOrgData(orgCode).pipe(take(1)).toPromise().catch(() => ({})),
      this.ads.getPatientAppointments(patientKey).pipe(take(1)).toPromise().catch(() => []),
      this.ads.getProvidersList().pipe(take(1)).toPromise().catch(() => []),
    ]);

    // 2. Resolve Appointment & Facility
    const selectedAppointment = this.resolveAppointment(appointments || [], options.appointmentKey);
    let facilityData: any = null;
    if (selectedAppointment?.facility) {
      facilityData = await this.resolveFacility(selectedAppointment.facility);
    }

    // 3. Resolve Treating Doctor
    const doctorData = this.resolveDoctor(
      selectedAppointment?.provider?.name || patient?.treatingDoctor,
      providers || []
    );

    // 4. Resolve Latest Encounter (SOAP + Physical Exam Form Data)
    const latestEncounter = await this.resolveLatestEncounter(patientKey, patient, options);

    // 5. Resolve Historian Forms (/patients/{key}/forms)
    const historyForms = options.includeHistoryForms !== false
      ? await this.resolveHistoryForms(patientKey, patient)
      : {};

    // 6. Resolve Workability & Work Status
    const workStatus = patient?.workability?.workStatus || {};

    // 7. Resolve Signatures (if requested)
    let userSignature: any = null;
    let doctorSignature: any = null;
    if (options.includeSignatures !== false) {
      try {
        const sigPromises: Promise<any>[] = [];
        if (user?.uid) {
          sigPromises.push(
            this.db.object(`provider-org-${orgCode}/esign-data/${user.uid}`).valueChanges().pipe(take(1)).toPromise().catch(() => null)
          );
        } else {
          sigPromises.push(Promise.resolve(null));
        }

        if (doctorData?.uid) {
          sigPromises.push(
            this.db.object(`provider-org-${orgCode}/esign-data/${doctorData.uid}`).valueChanges().pipe(take(1)).toPromise().catch(() => null)
          );
        } else {
          sigPromises.push(Promise.resolve(null));
        }

        const [uSig, dSig] = await Promise.all(sigPromises);
        userSignature = uSig;
        doctorSignature = dSig;
      } catch (e) {
        console.warn('[CaseDataComposerService] Error resolving signatures:', e);
      }
    }

    // 8. Package Raw Nodes & Execute Transformation
    const rawNodes: RawCaseNodes = {
      patient,
      orgData,
      doctorData,
      selectedAppointment,
      facilityData,
      latestEncounter,
      historyForms,
      workStatus,
      user,
      doctorSignature,
      userSignature,
    };

    return CaseDataTransformer.transform(rawNodes);
  }

  private resolveAppointment(appointments: any[], appointmentKey?: string): any {
    if (!appointments || appointments.length === 0) return null;

    if (appointmentKey) {
      const match = appointments.find((a) => a.id === appointmentKey || a.key === appointmentKey);
      if (match) return match;
    }

    // Filter active appointments (statusId === 0)
    const activeAppts = appointments.filter((a) => a.statusId === 0 || a.statusId === '0');
    const targetList = activeAppts.length > 0 ? activeAppts : appointments;

    // Sort descending by start timestamp
    const sorted = [...targetList].sort((a, b) => {
      const aTime = a.start ? new Date(a.start).getTime() : 0;
      const bTime = b.start ? new Date(b.start).getTime() : 0;
      return bTime - aTime;
    });

    return sorted[0] || null;
  }

  private async resolveFacility(facilityName: string): Promise<any> {
    if (!facilityName) return null;
    try {
      const facilities = await this.ads.getFacility(facilityName).pipe(take(1)).toPromise();
      return facilities && facilities.length > 0 ? facilities[0] : null;
    } catch (e) {
      console.warn('[CaseDataComposerService] Error resolving facility:', e);
      return null;
    }
  }

  private resolveDoctor(doctorName: string, providers: any[]): any {
    if (!doctorName) return null;
    if (!providers || providers.length === 0) return { name: doctorName, displayName: doctorName };

    const clean = doctorName.trim().toLowerCase();
    const match = providers.find((p) => {
      const pName = (p.displayName || p.name || '').trim().toLowerCase();
      return pName === clean || clean.includes(pName) || pName.includes(clean);
    });

    return match || { name: doctorName, displayName: doctorName };
  }

  private async resolveLatestEncounter(patientKey: string, patientData: any, options: CaseDataOptions): Promise<any> {
    const orgCode = this._as.get(AppVars.ORG_CODE);
    let encounters = patientData?.encounters;

    if (!encounters) {
      try {
        encounters = await this.db.object(`provider-org-${orgCode}/patients/${patientKey}/encounters`)
          .valueChanges().pipe(take(1)).toPromise();
      } catch (e) {
        console.warn('[CaseDataComposerService] Error fetching encounters:', e);
      }
    }

    const encounterList: any[] = [];
    if (encounters) {
      if (Array.isArray(encounters)) {
        encounterList.push(...encounters);
      } else if (typeof encounters === 'object') {
        Object.keys(encounters).forEach((key) => {
          encounterList.push({ id: key, key, ...encounters[key] });
        });
      }
    }

    if (encounterList.length === 0) return null;

    let targetEncounter: any = null;
    if (options.appointmentKey) {
      targetEncounter = encounterList.find((e) => e.id === options.appointmentKey || e.key === options.appointmentKey);
    }

    if (!targetEncounter) {
      // Sort descending by start date
      encounterList.sort((a, b) => {
        const aTime = a.start ? new Date(a.start).getTime() : 0;
        const bTime = b.start ? new Date(b.start).getTime() : 0;
        return bTime - aTime;
      });
      targetEncounter = encounterList[0];
    }

    if (!targetEncounter) return null;

    const resolved = { ...targetEncounter };

    // Ingest physical exam form-data if requested
    if (options.includePhysicalExamData !== false) {
      let formData = resolved['form-data'];
      if (!formData && resolved.id) {
        try {
          formData = await this.db.object(`provider-org-${orgCode}/patients/${patientKey}/encounters/${resolved.id}/form-data`)
            .valueChanges().pipe(take(1)).toPromise();
        } catch (e) {
          console.warn('[CaseDataComposerService] Error fetching encounter form-data:', e);
        }
      }
      if (formData && typeof formData === 'object') {
        delete resolved['form-data'];
        Object.keys(formData).forEach((formKey) => {
          resolved[formKey] = formData[formKey];
        });
        resolved.formData = formData;
      }
    }

    return resolved;
  }

  private async resolveHistoryForms(patientKey: string, patientData: any): Promise<{ [formName: string]: any }> {
    const orgCode = this._as.get(AppVars.ORG_CODE);
    let forms = patientData?.forms;

    if (!forms) {
      try {
        forms = await this.db.object(`provider-org-${orgCode}/patients/${patientKey}/forms`)
          .valueChanges().pipe(take(1)).toPromise();
      } catch (e) {
        console.warn('[CaseDataComposerService] Error fetching history forms:', e);
      }
    }

    return forms && typeof forms === 'object' ? forms : {};
  }
}
